import { markRaw } from 'vue'
import { CodebookValueNotFoundError } from './index'

/**
 * Base codebook class that provides convenience methods to access the instances
 *
 * Example usage:
 *
 *    export enum ExampleId {
 *      FOO = 1,
 *      BAR = 2
 *    }
 *
 *    export class Example extends Codebook<ExampleId> {
 *      static readonly FOO = new Example(ExampleId.FOO)
 *      static readonly BAR = new Example(ExampleId.BAR)
 *    }
 *
 *    Example.FOO
 *    Example.getById(ExampleId.FOO)
 *    Example.all()
 *
 * You can add additional properties too, just keep the constructor private:
 *
 *    export class Example extends Codebook<ExampleId> {
 *      static readonly FOO = new Example(ExampleId.FOO, () => $t('example.foo'), 123)
 *      static readonly BAR = new Example(ExampleId.FOO, () => $t('example.bar'), 456)
 *
 *      private constructor(
 *        id: ExampleId,
 *        public readonly name: () => string,
 *        public readonly someProperty: number
 *      ) {
 *        super(id)
 *      }
 *    }
 *
 *    Example.getById(ExampleId.FOO).someProperty // number
 */
export abstract class Codebook<TId extends CodebookIdValue> {
  /** Internal registry that holds all codebook instances */
  private static codebooks: CodebookRegistry = new Map()

  protected constructor(public readonly id: TId) {
    markRaw(this)

    let instances = Codebook.codebooks.get(this.constructor)

    if (instances === undefined) {
      instances = {
        map: new Map(),
        list: [],
      }

      Codebook.codebooks.set(this.constructor, instances)
    }

    if (instances.map.has(id)) {
      throw new Error(`${this.constructor.name}: Duplicate instantiation with value ${id}`)
    }

    instances.map.set(id, this)
    instances.list.push(this)
  }

  static getById<T extends CodebookInstance>(this: { prototype: T; name: string }, id: T['id']): T {
    const map = Codebook.codebooks.get(this)?.map as Map<T['id'], T>

    if (map === undefined) {
      throw new Error(`${this.name}: Cannot use codebook before initialization`)
    }

    const instance = map.get(id)

    if (instance === undefined) {
      throw new CodebookValueNotFoundError(this.name, id)
    }

    return instance
  }

  static contains<T extends CodebookInstance>(
    this: { prototype: T; name: string },
    id: T['id'],
  ): boolean {
    const map = Codebook.codebooks.get(this)?.map as Map<T['id'], T>

    if (map === undefined) {
      throw new Error(`${this.name}: Cannot use codebook before initialization`)
    }

    return map.has(id)
  }

  static all<T>(this: { prototype: T; name: string }): T[] {
    const list = Codebook.codebooks.get(this)?.list as T[]

    if (list === undefined) {
      throw new Error(`${this.name}: Cannot use codebook before initialization`)
    }

    return list
  }
}

export type CodebookClass<T extends Codebook<CodebookIdValue>> = {
  prototype: T
  name: string
  getById(id: T['id']): T
  contains(id: T['id']): boolean
  all(): T[]
}

type CodebookIdValue = number | string
type CodebookInstance = Codebook<CodebookIdValue>
type CodebookRegistry = Map<
  unknown,
  {
    map: Map<CodebookIdValue, CodebookInstance>
    list: CodebookInstance[]
  }
>
